import * as d3 from 'd3';
import React from "react";
import styled from 'styled-components';

import ChartDimensions from '../../models/ChartDimensions';
import ChartMargins from '../../models/ChartMargins';
import ChartPaddings from '../../models/ChartPaddings';

import Chartable from '../../interfaces/Chartable';
import Scalable from '../../interfaces/Scalable';
import VerticalAxis from './VerticalAxis';
import classNames from 'classnames';

const DIMENSIONS: ChartDimensions = {
  width: 720,
  height: 360,
};

const MARGINS: ChartMargins = {
  top: 20,
  left: 70,
  bottom: 200,
  right: 10,
};

const PADDINGS: ChartPaddings = {
  left: 30,
  right: 30,
};

const StyledBarChart = styled.svg`
  font-family: MapfreText-Bold;

  &.dark {
    stroke: #464646;

    text {
      stroke: none;
      fill: #464646;
    }
  }

  &.light {
    stroke: #FBFBFB;

    text {
      stroke: none;
      fill: #FBFBFB;
    }
  }

  & .bar-label {
    text-anchor: end;
    font-size: 0.8rem;
  }
`;

const StyledBar = styled.rect`
  stroke: none;
`;

const Point = styled.circle`
  fill: #FBFBFB;
  stroke: #CC0000;
  stroke-width: 2px;
`;

export interface BarData {
  group: string,
  bars: number[],
  point: number,
}

interface BarchartProps {
  bars: BarData[],
  isDark: boolean,
  barColors: string[],
  className?: string,
  formatVerticalTicks?: (n: number) => string,
}

interface BarProps extends Scalable, Chartable {
  index: number,
  data: BarData,
  barColors: string[],
}

const stackBars = (bars: number[]) => {
  const stackedBars = [];
  let positiveAcc = 0, negativeAcc = 0;
  for (let bar of bars) {
    if (bar >= 0) {
      stackedBars.push(positiveAcc + bar)
      positiveAcc += bar;
    } else {
      stackedBars.push(negativeAcc + bar);
      negativeAcc += bar;
    }
  }

  return stackedBars;
};

const Bar: React.FC<BarProps> = (props: BarProps) => {
  const barWidth = DIMENSIONS.width / 45;

  return (
    <React.Fragment key={ `bar-fragment-${props.data.group}` }>
      { props.data.bars
          .map((bar, i) => {
            return (
              <StyledBar
                x={ props.xScale(props.index) }
                y={ bar > 0 ? props.yScale(bar) : props.yScale(0) }
                rx={ 6 }
                ry={ 6 }
                width={ barWidth }
                height={ Math.abs(props.yScale(0) - props.yScale(bar)) }
                fill={ props.barColors[i] }
                key={ `bar${i}-${props.data.group}` }
              />
            );
          })
          .reverse()
      }
      <Point
          cx={ props.xScale(props.index) + barWidth / 2 }
          cy={ props.yScale(props.data.point) }
          r={ 3.5 }
          key={ `point-${props.data.group}` }
        />
      <text
          className='bar-label'
          x={ props.xScale(props.index) + barWidth / 2 }
          y={ MARGINS.top + DIMENSIONS.height + 10 }
          transform={`rotate(-90,${props.xScale(props.index) + barWidth / 2},${MARGINS.top + DIMENSIONS.height + 10})`}
          key={ `label-${props.data.group}` }
        >
        { props.data.group }
      </text>
    </React.Fragment>
  )
};

const Barchart: React.FC<BarchartProps> = (props: BarchartProps) => {
  const bars = props.bars;
  const stackedBars: BarData[] = bars.map((bar) => ({ group: bar.group, bars: stackBars(bar.bars), point: bar.point }));
  const valuesBars = stackedBars.flatMap((x) => x.bars);
  const valuesPoint = stackedBars.map(x => x.point);
  const allValues = valuesBars.concat(valuesPoint);

  const xScale = d3.scaleLinear()
    // @ts-ignore
    .domain([0, stackedBars.length + 1])
    .range([MARGINS.left + PADDINGS.left, MARGINS.left + DIMENSIONS.width - PADDINGS.right]);

  const yScale = d3.scaleLinear()
    // @ts-ignore
    .domain([Math.min(0, d3.min(allValues)), d3.max(allValues)])
    .range([MARGINS.top + DIMENSIONS.height, MARGINS.top]);

  const yTicks = yScale.nice().ticks();

  return (
    <StyledBarChart
        viewBox={`0 0 ${DIMENSIONS.width + MARGINS.left + MARGINS.right} ${DIMENSIONS.height + MARGINS.top + MARGINS.bottom}`}
        className={ classNames({
          dark: props.isDark,
          light: !props.isDark
        }, props.className) }
        preserveAspectRatio='xMidYMin meet'
      >
      <VerticalAxis
          xScale={ xScale }
          yScale={ yScale }
          ticks={ yTicks }
          dimensions={ DIMENSIONS }
          margins={ MARGINS }
          paddings={ PADDINGS }
          formatTicks={ props.formatVerticalTicks }
        />
      { stackedBars.map((bar, i) => {
          return (
            <Bar
                xScale={ xScale }
                yScale={ yScale }
                data={ bar }
                index={ i }
                dimensions={ DIMENSIONS }
                margins={ MARGINS }
                paddings={ PADDINGS }
                barColors={ props.barColors }
                key={ bar.group + i + Math.random() }
              />
          );
        })
      }
    </StyledBarChart>
  )
};

export default Barchart;
